import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { APP_CONFIG } from "./appConfig";
import { buildStripePaymentLinkUrl } from "./stripePaymentLink";
import { cloudFunctions } from "./firebase";

const PAYMENT_WAIT_MS = 120000;

/** Leesbare fouttekst van Firebase callable (HttpsError). */
function formatCallableError(err) {
  if (!err) return "Onbekende fout.";
  const code = String(err.code || err?.status || "");
  const msg = (err.message || "").trim();
  if (code.includes("not-found")) {
    return msg.length > 40 ? msg : "Geen betaalde Stripe-sessie gevonden voor dit account. Gebruik de betaalknop in deze app (client reference), of zet session_id in de Stripe-success-URL.";
  }
  if (code.includes("internal")) {
    return "Serverfout (INTERNAL). Deploy de nieuwste functions vanuit de map met firebase.json (o.a. confirmStripeLatestPaidSession) en controleer of Stripe secret test/live overeenkomt met je betaling.";
  }
  if (code.includes("unauthenticated")) {
    return "Je sessie is verlopen. Log opnieuw in en probeer opnieuw.";
  }
  if (code.includes("permission-denied")) {
    if (msg && msg.length > 20 && !msg.includes("App Check")) return msg;
    return msg || "Geen toestemming. Controleer App Check in Firebase, of of deze betaling bij je account hoort (e-mail / betaalknop).";
  }
  if (code.includes("failed-precondition")) {
    return msg || "Betaling is in Stripe nog niet op ‘betaald’; even wachten en opnieuw proberen.";
  }
  if (code.includes("resource-exhausted")) {
    return msg || "Te veel pogingen. Wacht even en probeer opnieuw.";
  }
  return msg || code || "Er ging iets mis.";
}

/** Stripe moet `{CHECKOUT_SESSION_ID}` vervangen door `cs_…`. Anders is de param onbruikbaar. */
function normalizeStripeSessionId(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  try {
    s = decodeURIComponent(s);
  } catch {
    /* ignore */
  }
  if (!s || /CHECKOUT_SESSION_ID/i.test(s)) return null;
  if (!s.startsWith("cs_")) return null;
  return s;
}

function isUnreplacedStripeSessionPlaceholder(raw) {
  if (raw == null || raw === "") return false;
  return /CHECKOUT_SESSION_ID/i.test(String(raw));
}

/** Voorkomt redirect-loop naar Stripe als success-URL geen ?payment=success heeft (webhook is dan nog bezig). */
const STRIPE_CHECKOUT_PENDING_KEY = "sparro_stripe_checkout_pending";
const CHECKOUT_PENDING_MAX_MS = 20 * 60 * 1000;

function readCheckoutPending() {
  if (typeof window === "undefined") return false;
  try {
    const t = sessionStorage.getItem(STRIPE_CHECKOUT_PENDING_KEY);
    if (!t) return false;
    const age = Date.now() - Number(t);
    if (age > CHECKOUT_PENDING_MAX_MS || age < 0) {
      sessionStorage.removeItem(STRIPE_CHECKOUT_PENDING_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function markCheckoutPending() {
  try {
    sessionStorage.setItem(STRIPE_CHECKOUT_PENDING_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading, accessLoading, hasPortalAccess, refreshUserEntitlement } = useAuth();
  const [paymentWaitExpired, setPaymentWaitExpired] = useState(false);
  const [sessionCheckoutFailed, setSessionCheckoutFailed] = useState(false);

  const hasStripeUrl = (APP_CONFIG.stripeUnlimitedUrl || "").trim().length > 0;
  const useSessionCheckout = APP_CONFIG.stripeCheckoutSession && !!cloudFunctions;
  /** Payment Link URL and/or Checkout Session via Functions */
  const stripeReady = useSessionCheckout || hasStripeUrl;
  const paymentSuccess = searchParams.get("payment") === "success";
  const stayOnPage = searchParams.get("stay") === "1";
  const rawSessionIdParam = searchParams.get("session_id");
  const sessionIdFromStripe = useMemo(() => normalizeStripeSessionId(rawSessionIdParam), [rawSessionIdParam]);
  const unreplacedSessionPlaceholder = useMemo(
    () => isUnreplacedStripeSessionPlaceholder(rawSessionIdParam),
    [rawSessionIdParam]
  );
  const checkoutPending = readCheckoutPending();
  const [confirmStripeError, setConfirmStripeError] = useState(null);
  const [confirmStripeWorking, setConfirmStripeWorking] = useState(false);
  const stripeConfirmInFlight = useRef(false);

  /**
   * Success-URL voor Stripe: altijd het adres van **dit tabblad** (niet REACT_APP_PUBLIC_SITE_URL).
   * Anders staat er op je telefoon per ongeluk localhost in het voorbeeld terwijl je op 192.168… test.
   */
  const stripeAfterPaymentUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin.replace(/\/$/, "")}/billing?payment=success&session_id={CHECKOUT_SESSION_ID}`;
  }, []);

  /** Bevestig betaling via Stripe API (fallback als webhook lokaal/LAN niet aankomt). */
  const runConfirmStripeSession = useCallback(async () => {
    const sid = sessionIdFromStripe;
    if (!sid || !cloudFunctions || !user?.uid) {
      setConfirmStripeError(
        "Geen session id in de URL. Zet in Stripe bij After payment de URL met &session_id={CHECKOUT_SESSION_ID} (zie uitleg op /billing)."
      );
      return;
    }
    if (stripeConfirmInFlight.current) return;
    stripeConfirmInFlight.current = true;
    setConfirmStripeWorking(true);
    setConfirmStripeError(null);
    const maxAttempts = 6;
    const delayMs = 2000;
    try {
      const fn = cloudFunctions.httpsCallable("confirmStripeCheckoutSession");
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          await fn({ sessionId: sid });
          await refreshUserEntitlement();
          return;
        } catch (e) {
          const code = String(e?.code || "");
          const msg = (e?.message || "").toLowerCase();
          const waiting =
            code.includes("failed-precondition") || msg.includes("nog niet voltooid") || msg.includes("unpaid");
          if (waiting && attempt < maxAttempts - 1) {
            await new Promise((r) => setTimeout(r, delayMs));
            continue;
          }
          if (code.includes("permission-denied")) {
            try {
              const fnLatest = cloudFunctions.httpsCallable("confirmStripeLatestPaidSession");
              await fnLatest();
              await refreshUserEntitlement();
              return;
            } catch (e2) {
              setConfirmStripeError(formatCallableError(e2));
              return;
            }
          }
          setConfirmStripeError(formatCallableError(e));
          return;
        }
      }
    } finally {
      stripeConfirmInFlight.current = false;
      setConfirmStripeWorking(false);
    }
  }, [sessionIdFromStripe, user?.uid, refreshUserEntitlement]);

  /** Als Stripe alleen ?payment=success terugstuurt (geen session_id), zoek de laatste betaalde sessie met jouw client_reference_id. */
  const confirmLatestPaidFromStripe = useCallback(async () => {
    if (!cloudFunctions || !user?.uid) return;
    setConfirmStripeWorking(true);
    setConfirmStripeError(null);
    try {
      const fn = cloudFunctions.httpsCallable("confirmStripeLatestPaidSession");
      await fn();
      await refreshUserEntitlement();
    } catch (e) {
      setConfirmStripeError(formatCallableError(e));
    } finally {
      setConfirmStripeWorking(false);
    }
  }, [user?.uid, refreshUserEntitlement]);

  useEffect(() => {
    if (!paymentSuccess || !user?.uid || hasPortalAccess || !sessionIdFromStripe || !cloudFunctions) return;
    runConfirmStripeSession();
  }, [paymentSuccess, user?.uid, hasPortalAccess, sessionIdFromStripe, runConfirmStripeSession]);

  useEffect(() => {
    if (!paymentSuccess || !user?.uid || hasPortalAccess || sessionIdFromStripe || !cloudFunctions) return;
    confirmLatestPaidFromStripe();
  }, [paymentSuccess, user?.uid, hasPortalAccess, sessionIdFromStripe, confirmLatestPaidFromStripe]);

  const isLikelyLanDev =
    typeof window !== "undefined" &&
    /^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[01])\./.test(window.location.hostname);

  const shouldRedirectToStripe = useMemo(() => {
    if (!user || loading || accessLoading || hasPortalAccess) return false;
    if (!stripeReady || paymentSuccess || stayOnPage) return false;
    if (sessionCheckoutFailed) return false;
    if (APP_CONFIG.stripeCheckoutSession && !cloudFunctions) return false;
    if (!APP_CONFIG.stripeCheckoutSession && !hasStripeUrl) return false;
    // Net terug van Stripe zonder ?payment=success: wacht op webhook, niet opnieuw naar Stripe sturen.
    if (checkoutPending) return false;
    return true;
  }, [
    user,
    loading,
    accessLoading,
    hasPortalAccess,
    stripeReady,
    paymentSuccess,
    stayOnPage,
    checkoutPending,
    sessionCheckoutFailed,
    hasStripeUrl,
  ]);

  const startStripeCheckout = useCallback(async () => {
    setSessionCheckoutFailed(false);
    markCheckoutPending();
    // Altijd het adres waar de gebruiker de app opent (localhost, 192.168…, productie) — niet REACT_APP_PUBLIC_SITE_URL.
    const origin = typeof window !== "undefined" ? window.location.origin.replace(/\/$/, "") : "";
    if (useSessionCheckout) {
      try {
        const fn = cloudFunctions.httpsCallable("createStripeCheckoutSession");
        const res = await fn({ origin });
        const url = res?.data?.url;
        if (url) window.location.assign(url);
        else setSessionCheckoutFailed("Geen checkout-URL ontvangen.");
      } catch (e) {
        const msg = e?.message || e?.code || "Checkout start mislukt.";
        setSessionCheckoutFailed(String(msg));
        try {
          sessionStorage.removeItem(STRIPE_CHECKOUT_PENDING_KEY);
        } catch {
          /* ignore */
        }
      }
      return;
    }
    if (!user?.uid) return;
    const url = buildStripePaymentLinkUrl(APP_CONFIG.stripeUnlimitedUrl.trim(), user.uid);
    if (url) window.location.assign(url);
  }, [useSessionCheckout, user?.uid]);

  useEffect(() => {
    if (!hasPortalAccess) return;
    try {
      sessionStorage.removeItem(STRIPE_CHECKOUT_PENDING_KEY);
    } catch {
      /* ignore */
    }
  }, [hasPortalAccess]);

  useEffect(() => {
    if ((!paymentSuccess && !checkoutPending) || hasPortalAccess) return;
    const t = window.setTimeout(() => setPaymentWaitExpired(true), PAYMENT_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [paymentSuccess, checkoutPending, hasPortalAccess]);

  useEffect(() => {
    if (!shouldRedirectToStripe || !user?.uid) return;
    if (useSessionCheckout) {
      let cancelled = false;
      startStripeCheckout().then(() => {
        if (cancelled) return;
      });
      return () => {
        cancelled = true;
      };
    }
    const url = buildStripePaymentLinkUrl(APP_CONFIG.stripeUnlimitedUrl.trim(), user.uid);
    if (!url) return;
    markCheckoutPending();
    window.location.replace(url);
  }, [shouldRedirectToStripe, user?.uid, useSessionCheckout, startStripeCheckout]);

  useEffect(() => {
    if (!loading && !accessLoading && hasPortalAccess) {
      navigate("/portal", { replace: true });
    }
  }, [loading, accessLoading, hasPortalAccess, navigate]);

  /** Webhook / snapshot can be slow on mobile; poll Firestore until paid or timeout. */
  useEffect(() => {
    if (!paymentSuccess || !user?.uid || hasPortalAccess || !refreshUserEntitlement) return;
    let n = 0;
    const max = 48;
    const id = window.setInterval(async () => {
      n += 1;
      if (n > max) {
        window.clearInterval(id);
        return;
      }
      await refreshUserEntitlement();
    }, 2500);
    refreshUserEntitlement();
    return () => window.clearInterval(id);
  }, [paymentSuccess, user?.uid, hasPortalAccess, refreshUserEntitlement]);

  if (loading || accessLoading) {
    return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>Laden...</div>;
  }

  if (!user) return <Navigate to="/signup" replace />;

  if ((paymentSuccess || checkoutPending) && !hasPortalAccess) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)", padding: 24 }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", maxWidth: 440, textAlign: "center", boxShadow: "0 20px 40px rgba(0,0,0,0.12)" }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#111827" }}>Betaling verwerken…</p>
          <p style={{ margin: "12px 0 0", fontSize: 14, color: "#6B7280", lineHeight: 1.5 }}>
            Je toegang wordt geactiveerd zodra Stripe de betaling heeft bevestigd. Dit duurt meestal enkele seconden.
          </p>
          {confirmStripeError ? (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 10,
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                fontSize: 13,
                color: "#991B1B",
                lineHeight: 1.5,
                textAlign: "left",
              }}
            >
              {confirmStripeError}
            </div>
          ) : null}
          {sessionIdFromStripe ? (
            <p style={{ margin: "10px 0 0", fontSize: 12, color: "#374151", lineHeight: 1.45, textAlign: "left" }}>
              <strong>session_id</strong> in URL: ok (<code style={{ fontSize: 11, wordBreak: "break-all" }}>…{sessionIdFromStripe.slice(-12)}</code>)
            </p>
          ) : null}
          {user?.uid ? (
            <p style={{ margin: "6px 0 0", fontSize: 12, color: "#374151", lineHeight: 1.45, textAlign: "left" }}>
              <strong>Jouw account-id</strong> (moet gelijk zijn aan <em>Client reference</em> in Stripe bij deze betaling):{" "}
              <code style={{ fontSize: 10, wordBreak: "break-all" }}>{user.uid}</code>
            </p>
          ) : null}
          {!cloudFunctions ? (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 10,
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                fontSize: 13,
                color: "#991B1B",
                lineHeight: 1.55,
                textAlign: "left",
              }}
            >
              <strong>Geen Firebase in deze build.</strong> Je ziet een <strong>mock</strong> account-id (zoals <code style={{ fontSize: 11 }}>mock-…</code>) als{" "}
              <code style={{ fontSize: 11 }}>sparro/.env</code> geen echte <code style={{ fontSize: 11 }}>REACT_APP_FIREBASE_*</code> waarden heeft. Zonder Firebase zijn er geen Cloud Functions — Stripe kan je betaling hier niet bevestigen.
              <br />
              <br />
              <strong>Oplossing:</strong> kopieer <code style={{ fontSize: 11 }}>.env.example</code> naar <code style={{ fontSize: 11 }}>.env</code>, vul de Firebase-webconfig van project <strong>sparro-152b5</strong> (Console → Projectinstellingen → Jouw apps), sla op, <strong>herstart</strong>{" "}
              <code style={{ fontSize: 11 }}>npm start</code>, log opnieuw in met een echt account (niet mock).
            </div>
          ) : null}
          {unreplacedSessionPlaceholder ? (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 10,
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                fontSize: 13,
                color: "#991B1B",
                lineHeight: 1.5,
                textAlign: "left",
              }}
            >
              In de adresbalk staat nog de <strong>placeholder</strong> (CHECKOUT_SESSION_ID) i.p.v. een id dat begint met{" "}
              <code style={{ fontSize: 11 }}>cs_</code>. Stripe heeft de placeholder niet vervangen — controleer in Stripe of de redirect-URL eindigt op{" "}
              <code style={{ fontSize: 11, wordBreak: "break-all" }}>{`session_id={CHECKOUT_SESSION_ID}`}</code>{" "}
              (letterlijke placeholder zoals Stripe die invult). Betrouwbaarder op telefoon + PC: zet in{" "}
              <code style={{ fontSize: 11 }}>.env</code> <code style={{ fontSize: 11 }}>REACT_APP_STRIPE_CHECKOUT_SESSION=true</code> en{" "}
              <code style={{ fontSize: 11 }}>STRIPE_PRICE_ID</code> op de server — checkout start dan vanaf deze site met de juiste success-URL.
            </div>
          ) : null}
          {!useSessionCheckout && hasStripeUrl && isLikelyLanDev ? (
            <p style={{ margin: "12px 0 0", fontSize: 13, color: "#1E40AF", lineHeight: 1.5, textAlign: "left" }}>
              <strong>LAN / mobiel:</strong> met alleen een Payment Link moet je in Stripe bij <strong>After payment</strong> exact de URL hieronder gebruiken (zelfde host als in je adresbalk). Nog betrouwbaarder:{" "}
              <code style={{ fontSize: 11 }}>REACT_APP_STRIPE_CHECKOUT_SESSION=true</code> + prijs-id op Firebase Functions.
            </p>
          ) : null}
          {!sessionIdFromStripe && cloudFunctions && confirmStripeWorking ? (
            <p style={{ margin: "12px 0 0", fontSize: 13, color: "#374151", lineHeight: 1.45, textAlign: "left" }}>
              Er staat geen <code style={{ fontSize: 11 }}>session_id</code> in de URL; we zoeken je betaalde sessie bij Stripe op basis van je account (zoals bij de betaalknop in deze app).
            </p>
          ) : null}
          {!sessionIdFromStripe && cloudFunctions ? (
            <p style={{ margin: "14px 0 0", fontSize: 13, color: "#92400E", lineHeight: 1.5, textAlign: "left" }}>
              <strong>Aanbevolen in Stripe:</strong> zet bij <strong>After payment</strong> deze URL (met{" "}
              <code style={{ fontSize: 11 }}>session_id</code>), dan werkt het ook zonder deze stap:{" "}
              <code style={{ fontSize: 11, wordBreak: "break-all", display: "block", marginTop: 8 }}>{stripeAfterPaymentUrl}</code>
            </p>
          ) : null}
          {sessionIdFromStripe && cloudFunctions ? (
            <button
              type="button"
              disabled={confirmStripeWorking}
              onClick={() => runConfirmStripeSession()}
              style={{
                marginTop: 16,
                padding: "10px 18px",
                borderRadius: 10,
                border: "none",
                background: confirmStripeWorking ? "#9ca3af" : "#3399FF",
                color: "#fff",
                fontWeight: 600,
                cursor: confirmStripeWorking ? "wait" : "pointer",
                fontSize: 14,
              }}
            >
              {confirmStripeWorking ? "Bezig…" : "Opnieuw toegang activeren"}
            </button>
          ) : null}
          {!sessionIdFromStripe && cloudFunctions ? (
            <button
              type="button"
              disabled={confirmStripeWorking}
              onClick={() => confirmLatestPaidFromStripe()}
              style={{
                marginTop: sessionIdFromStripe ? 8 : 16,
                padding: "10px 18px",
                borderRadius: 10,
                border: "none",
                background: confirmStripeWorking ? "#9ca3af" : "#3399FF",
                color: "#fff",
                fontWeight: 600,
                cursor: confirmStripeWorking ? "wait" : "pointer",
                fontSize: 14,
              }}
            >
              {confirmStripeWorking ? "Bezig…" : "Opnieuw zoeken bij Stripe"}
            </button>
          ) : null}
          {paymentWaitExpired ? (
            <div style={{ margin: "16px 0 0", fontSize: 13, color: "#B45309", lineHeight: 1.5, textAlign: "left" }}>
              <p style={{ margin: 0 }}>
                Het duurt langer dan verwacht. Meestal ontbreekt de koppeling tussen Stripe en jouw account, of de webhook bereikt je server niet (normaal op een lokaal IP).
              </p>
              <ol style={{ margin: "10px 0 0", paddingLeft: 20 }}>
                <li style={{ marginBottom: 6 }}>
                  Tap <strong>Opnieuw zoeken bij Stripe</strong> hierboven (of voeg in Stripe bij After payment toe:{" "}
                  <code style={{ fontSize: 11 }}>{`&session_id={CHECKOUT_SESSION_ID}`}</code>).
                </li>
                <li style={{ marginBottom: 6 }}>
                  Start de betaling via de <strong>Open Stripe-checkout</strong>-knop in deze app (niet een opgeslagen buy.stripe.com-link). Dan staat in Stripe bij de sessie <strong>Client reference</strong> gelijk aan jouw account-id:
                </li>
              </ol>
              <code
                style={{
                  fontSize: 11,
                  wordBreak: "break-all",
                  display: "block",
                  marginTop: 4,
                  padding: 8,
                  background: "#FFFBEB",
                  borderRadius: 8,
                  border: "1px solid #FCD34D",
                }}
              >
                {user?.uid || "—"}
              </code>
              <p style={{ margin: "12px 0 0", fontSize: 12, color: "#92400E" }}>
                In Stripe Dashboard → <strong>Payments</strong> → jouw betaling → open de <strong>Checkout-sessie</strong> en controleer of <strong>Client reference</strong> exact dit id is. Zo niet: opnieuw betalen via de knop in de app.
              </p>
              <p style={{ margin: "10px 0 0", fontSize: 12, color: "#92400E" }}>
                Optioneel: <strong>Developers → Webhooks</strong> naar <code style={{ fontSize: 11 }}>stripeWebhook</code> (alleen nodig als je niet via session_id / “zoek sessie” werkt).
              </p>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (shouldRedirectToStripe) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)", padding: 24 }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", maxWidth: 420, textAlign: "center", boxShadow: "0 20px 40px rgba(0,0,0,0.12)" }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#111827" }}>Je wordt doorgestuurd naar Stripe…</p>
          <p style={{ margin: "12px 0 0", fontSize: 14, color: "#6B7280", lineHeight: 1.5 }}>
            Op de Stripe-pagina kun je betalen met <strong>iDEAL</strong> (en andere door jou ingeschakelde methodes) en eventueel een promotiecode gebruiken.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)", padding: 24 }}>
      <div style={{ maxWidth: 520, margin: "24px auto", background: "#fff", borderRadius: 20, padding: 24 }}>
        <h1 style={{ marginTop: 0, marginBottom: 8, color: "#111827" }}>Activeer onbeperkte toegang</h1>
        <p style={{ marginTop: 0, color: "#4B5563" }}>
          {stripeReady && stayOnPage
            ? "Doorverwijzing naar Stripe is uitgeschakeld (je zit op deze pagina met ?stay=1)."
            : "Volg de stappen hieronder om betaling of promotiecodes via Stripe in te stellen."}
        </p>

        {APP_CONFIG.stripeCheckoutSession && !cloudFunctions && (
          <div
            style={{
              marginBottom: 16,
              padding: 14,
              borderRadius: 12,
              background: "#FEE2E2",
              border: "1px solid #FCA5A5",
              fontSize: 13,
              color: "#991B1B",
              lineHeight: 1.5,
            }}
          >
            <strong>Checkout Session</strong> staat aan in .env, maar Firebase Functions zijn niet beschikbaar (bijv. mock-auth). Zet{" "}
            <code style={{ fontSize: 12 }}>REACT_APP_STRIPE_CHECKOUT_SESSION=false</code> of gebruik een Payment Link.
          </div>
        )}

        {useSessionCheckout && stripeAfterPaymentUrl && (
          <div
            style={{
              marginBottom: 16,
              padding: 14,
              borderRadius: 12,
              background: "#EFF6FF",
              border: "1px solid #93C5FD",
              fontSize: 13,
              color: "#1E3A8A",
              lineHeight: 1.5,
            }}
          >
            <strong style={{ display: "block", marginBottom: 8 }}>Dynamische terugkeer (PC én telefoon)</strong>
            Met <strong>Checkout Session</strong> bepaalt de app zelf de terugkeer-URL op basis van waar je nu bent:{" "}
            <code style={{ fontSize: 12, wordBreak: "break-all" }}>{stripeAfterPaymentUrl}</code>
            <span style={{ display: "block", marginTop: 10 }}>
              Je hoeft in Stripe <strong>geen aparte</strong> &quot;After payment&quot;-URL per apparaat in te stellen voor deze modus. Zet wel <code style={{ fontSize: 12 }}>STRIPE_PRICE_ID</code> op de server (zie documentatie).
            </span>
          </div>
        )}

        {!useSessionCheckout && hasStripeUrl && stripeAfterPaymentUrl && (
          <div
            style={{
              marginBottom: 16,
              padding: 14,
              borderRadius: 12,
              background: "#FFFBEB",
              border: "1px solid #FCD34D",
              fontSize: 13,
              color: "#92400E",
              lineHeight: 1.5,
            }}
          >
            <strong style={{ display: "block", marginBottom: 8 }}>Stripe Payment Link: redirect na betaling</strong>
            In Stripe Dashboard → deze Payment Link → <strong>After payment</strong> moet <strong>exact</strong> hetzelfde adres staan als hieronder (zelfde host als in je adresbalk). Er is maar <strong>één</strong> veld — kies je echte productie-URL; voor testen op LAN gebruik je tijdelijk dat LAN-adres of een tweede Payment Link.
            <code style={{ fontSize: 12, wordBreak: "break-all", display: "block", marginTop: 8, padding: 8, background: "#fff", borderRadius: 8 }}>
              {stripeAfterPaymentUrl}
            </code>
            <span style={{ display: "block", marginTop: 10 }}>
              Telefoon via <code style={{ fontSize: 12 }}>192.168.…</code> maar Stripe op <code style={{ fontSize: 12 }}>localhost</code>? Dat faalt — zelfde host overal, of schakel <code style={{ fontSize: 12 }}>REACT_APP_STRIPE_CHECKOUT_SESSION=true</code> in (zie blauwe uitleg).
            </span>
            {isLikelyLanDev && (
              <span style={{ display: "block", marginTop: 10, fontWeight: 600 }}>
                Je zit nu op een lokaal netwerk-adres — gebruik in Stripe precies:{" "}
                <code style={{ fontSize: 11, wordBreak: "break-all" }}>{stripeAfterPaymentUrl}</code>
              </span>
            )}
          </div>
        )}

        <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 4 }}>Onbeperkte toegang</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#111827", marginBottom: 12 }}>€9,99</div>
          {sessionCheckoutFailed ? (
            <p style={{ margin: "0 0 12px", fontSize: 14, color: "#B91C1C", lineHeight: 1.55 }}>{sessionCheckoutFailed}</p>
          ) : null}
          {stripeReady ? (
            useSessionCheckout ? (
              <>
                <p style={{ margin: "0 0 12px", fontSize: 14, color: "#374151", lineHeight: 1.55 }}>
                  Normaal word je automatisch naar Stripe gestuurd. Lukt dat niet, open de betaalpagina handmatig:
                </p>
                <button
                  type="button"
                  onClick={() => startStripeCheckout()}
                  style={{
                    display: "inline-block",
                    background: "#3399FF",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    padding: "12px 16px",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 16,
                  }}
                >
                  Open Stripe-checkout
                </button>
                <p style={{ margin: "12px 0 0", fontSize: 13, color: "#6B7280", lineHeight: 1.5 }}>
                  iDEAL en promotiecodes stel je in bij het <strong>Product / prijs</strong> dat aan <code style={{ fontSize: 12 }}>STRIPE_PRICE_ID</code> hangt in Stripe.
                </p>
              </>
            ) : (
              <>
                <p style={{ margin: "0 0 12px", fontSize: 14, color: "#374151", lineHeight: 1.55 }}>
                  Normaal word je automatisch naar Stripe gestuurd. Open de betaalpagina handmatig:
                </p>
                <a
                  href={user?.uid ? buildStripePaymentLinkUrl(APP_CONFIG.stripeUnlimitedUrl, user.uid) : APP_CONFIG.stripeUnlimitedUrl}
                  rel="noopener noreferrer"
                  onClick={() => markCheckoutPending()}
                  style={{
                    display: "inline-block",
                    background: "#3399FF",
                    color: "#fff",
                    textDecoration: "none",
                    borderRadius: 10,
                    padding: "12px 16px",
                    fontWeight: 600,
                  }}
                >
                  Open Stripe-checkout
                </a>
                <p style={{ margin: "12px 0 0", fontSize: 13, color: "#6B7280", lineHeight: 1.5 }}>
                  Zorg in Stripe bij deze Payment Link dat de <strong>After payment</strong>-URL exact onderstaande is (inclusief{" "}
                  <code style={{ fontSize: 11 }}>session_id</code> — zonder die parameter blijft de app op &quot;Betaling verwerken…&quot; hangen als de webhook niet aankomt, bv. op mobiel in je LAN):{" "}
                  <code style={{ fontSize: 12, wordBreak: "break-all", display: "block", marginTop: 6 }}>{stripeAfterPaymentUrl}</code>
                  Zet <strong>iDEAL</strong> aan bij betaalmethodes, en desgewenst <strong>promotiecodes</strong>. De app voegt automatisch{" "}
                  <code style={{ fontSize: 12 }}>client_reference_id</code> toe zodat de betaling aan jouw account wordt gekoppeld.
                </p>
              </>
            )
          ) : (
            <div
              style={{
                background: "#F9FAFB",
                border: "1px solid #E5E7EB",
                borderRadius: 10,
                padding: 14,
                fontSize: 14,
                color: "#374151",
                lineHeight: 1.55,
              }}
            >
              <strong style={{ display: "block", marginBottom: 10, color: "#111827" }}>
                Stripe instellen (eenmalig, ±2 minuten)
              </strong>
              <ol style={{ margin: "0 0 12px", paddingLeft: 20 }}>
                <li style={{ marginBottom: 8 }}>
                  Ga naar{" "}
                  <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" style={{ color: "#3399FF" }}>
                    dashboard.stripe.com
                  </a>{" "}
                  en log in.
                </li>
                <li style={{ marginBottom: 8 }}>
                  Kies links <strong>Payment Links</strong> (of <strong>Product catalog</strong> → <strong>Payment links</strong> → <strong>New</strong>).
                </li>
                <li style={{ marginBottom: 8 }}>
                  Maak een <strong>nieuw product</strong> of kies bestaand: prijs <strong>€9,99</strong> in <strong>EUR</strong>, <strong>eenmalig</strong> (niet per maand). iDEAL werkt alleen met EUR.
                </li>
                <li style={{ marginBottom: 8 }}>
                  Open <strong>Payment methods</strong> / <strong>Betaalmethodes</strong> voor deze link en schakel <strong>iDEAL</strong> in (naast bv. kaart). Dit is verplicht voor veel Nederlandse gebruikers; zonder iDEAL zie je het niet in checkout.
                </li>
                <li style={{ marginBottom: 8 }}>
                  Zet <strong>Allow promotion codes</strong> aan als je kortingscodes op de Stripe-betaalpagina wilt (in plaats van in deze app).
                </li>
                <li style={{ marginBottom: 8 }}>
                  Bij <strong>After payment</strong> / doorverwijzing: kies redirect en vul exact in (pas het domein aan naar je live site;{" "}
                  <strong>geen localhost</strong> voor echte gebruikers op mobiel):{" "}
                  <code style={{ fontSize: 12, wordBreak: "break-all", display: "block", marginTop: 6 }}>
                    {stripeAfterPaymentUrl ||
                      "https://jouw-domein.nl/billing?payment=success&session_id={CHECKOUT_SESSION_ID}"}
                  </code>
                </li>
                <li style={{ marginBottom: 8 }}>
                  Sla de Payment Link op en kopieer de <strong>volledige link</strong> (begint vaak met <code style={{ fontSize: 12 }}>https://buy.stripe.com/...</code>).
                </li>
                <li>
                  Maak in de map <code style={{ fontSize: 12 }}>sparro/</code> een bestand <code style={{ fontSize: 12 }}>.env</code> (kopieer van{" "}
                  <code style={{ fontSize: 12 }}>.env.example</code>) en zet:{" "}
                  <code style={{ fontSize: 12, display: "block", marginTop: 6 }}>
                    REACT_APP_STRIPE_UNLIMITED_URL=jouw_gekopieerde_link
                  </code>
                  Herstart daarna <code style={{ fontSize: 12 }}>npm start</code>. Deploy daarna Firebase Functions + webhook (zie <code style={{ fontSize: 12 }}>SECURITY_DEPLOY.md</code>).
                </li>
              </ol>
            </div>
          )}
        </div>

        <div style={{ marginTop: 20 }}>
          <Link to="/login" style={{ color: "#3399FF" }}>Uitloggen en wisselen van account</Link>
        </div>
      </div>
    </div>
  );
}
